import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {FeatureHighlight} from 'shells/ai-homepage/feature-highlight/feature-highlight';
import {PageHeader} from 'shells/ai-homepage/page-header/page-header';
import {AiHomepageHeader} from 'shells/ai-homepage/text/ai-homepage-header';
import {KeyTriggeredAnimateIn} from 'shells/animation/key-triggered-animate-in';
import {Button} from 'shells/button/button';
import {GridList} from 'shells/grid-list/grid-list';
import {Stack} from 'shells/layout/stack/stack';

import type {AiFeaturePeriod} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {AiFeatureGateModal} from '../ai-feature-gate-modal/ai-feature-gate-modal';
import {AiPageWrapper} from '../ai-page-wrapper';
import {getFeature, AI_FEATURES_CONFIG_MAP} from '../features/features-list';
import {useGetAiFeature} from '../graphql/hooks/use-get-ai-feature';
import {handleSelectedUsageTabChange, getSelectedUsageTab} from '../helpers';
import {getNumberOfStatColumns} from '../usage/helpers';
import {UsageSection} from '../usage/usage-section';

import {FeatureGrid} from './feature-grid';
import {FeaturePageNavHeader} from './feature-page-nav-header';
import type {AiFeature} from './types';

type Props = {
    feature: AiFeature;
} & ContextRouter;

export function FeaturePage(props: Props) {
    const selectedUsageTab = getSelectedUsageTab(props.location.search);

    const {aiFeature, isLoading} = useGetAiFeature(props.feature.type, selectedUsageTab);

    React.useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_AI_HOMEPAGE_FEATURE_VIEWED, {
            feature: props.feature.type,
        });
    }, [props.feature.type]);

    const numberOfStatColumns = React.useMemo(() => {
        return getNumberOfStatColumns(props.feature.type);
    }, [props.feature.type]);

    const relatedFeatures = React.useMemo(
        () =>
            props.feature.otherFeatures
                // @ts-expect-error
                .filter((featureType) => AI_FEATURES_CONFIG_MAP.has(featureType))
                .map(getFeature),
        [props.feature.otherFeatures]
    );

    const aiFeatureStats = React.useMemo(() => {
        return aiFeature && aiFeature.__typename === 'AiFeatureWithStats' ? aiFeature.stats : [];
    }, [aiFeature]);

    const shouldShowUpgradeButton = NutClientConfig.hasAiOutcomesFF && aiFeature?.requiresUpgrade;
    const [showFeatureGateModal, setShowFeatureGateModal] = React.useState<boolean>(false);

    return (
        <AiPageWrapper
            tabTitle={`AI · ${props.feature.name}`}
            header={
                <PageHeader
                    title={props.feature.name}
                    subTitle={props.feature.pageDescription}
                    illustration={props.feature.pageIllustration}
                    accent={props.feature.icon}
                    navHeader={
                        <FeaturePageNavHeader
                            feature={props.feature}
                            requiresUpgrade={shouldShowUpgradeButton}
                        />
                    }
                    action={
                        shouldShowUpgradeButton && (
                            <div className='pt-8'>
                                <Button
                                    variant='ai-purple-dark'
                                    size='big'
                                    onClick={() => setShowFeatureGateModal(true)}
                                >
                                    Upgrade to unlock
                                </Button>
                                <AiFeatureGateModal
                                    isOpen={showFeatureGateModal}
                                    onClose={() => setShowFeatureGateModal(false)}
                                    reason='notInPlan'
                                    featureName={props.feature.name}
                                    aiFeatureType={props.feature.type}
                                />
                            </div>
                        )
                    }
                />
            }
        >
            <Stack spacing={32}>
                {!props.feature.hasNoStats && (
                    <UsageSection
                        hasAiUsage={true}
                        isLoadingHasAiUsage={false}
                        statsSectionProps={{
                            aiFeatureStats,
                            numStatColumns: numberOfStatColumns,
                            isLoadingData: isLoading,
                        }}
                        tabProps={{
                            selectedTab: selectedUsageTab,
                            setSelectedTab: (period: AiFeaturePeriod) =>
                                handleSelectedUsageTabChange(
                                    period,
                                    props.history,
                                    props.location.pathname
                                ),
                        }}
                        featureType={props.feature.type}
                    />
                )}
                {/* @ts-expect-error */}
                <KeyTriggeredAnimateIn triggerKey={props.feature.type}>
                    <Stack spacing={32}>
                        {props.feature.highlights.length > 0 && (
                            <Stack isFullWidth={true} spacing={8}>
                                <AiHomepageHeader text='Features' />
                                <div className='pt-16'>
                                    <GridList numColumns={2}>
                                        {/* @ts-expect-error */}
                                        {props.feature.highlights.map((highlight) => (
                                            <FeatureHighlight
                                                key={highlight.title}
                                                title={highlight.title}
                                                description={highlight.description}
                                                icon={highlight.icon}
                                            />
                                        ))}
                                    </GridList>
                                </div>
                            </Stack>
                        )}
                        {relatedFeatures.length > 0 && (
                            <Stack isFullWidth={true} spacing={16}>
                                <AiHomepageHeader text='Explore other features' />
                                <FeatureGrid
                                    features={relatedFeatures}
                                    selectedUsageTab={selectedUsageTab}
                                />
                            </Stack>
                        )}
                    </Stack>
                </KeyTriggeredAnimateIn>
            </Stack>
        </AiPageWrapper>
    );
}
